/**
 * VoiceTutorLiveModal — Gemini Multimodal Live API version.
 * Full-duplex voice conversation: AI hears you, you hear AI, interruption handled natively.
 * Falls back silently if connection fails.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, GraduationCap, ChevronUp, ChevronDown, Volume2, VolumeX, Zap } from 'lucide-react';
import { GoogleGenAI } from '@google/genai';
import { getVoiceTutorLiveToken } from '../services/geminiService';
import { Language } from '../types';

interface Props {
    nodeTitle: string;
    nodeContent: string;
    language: Language;
    onClose: () => void;
    onFallback: () => void; // called when Live fails, parent switches to basic mode
}

type Phase =
    | 'connecting'   // fetching token + opening WebSocket
    | 'listening'    // AI done, mic open, waiting for user
    | 'user-speaking'// VAD detected user speech
    | 'ai-speaking'  // receiving + playing AI audio
    | 'error';

const LIVE_MODEL = 'models/gemini-live-2.5-flash-preview';

export const VoiceTutorLiveModal: React.FC<Props> = ({
    nodeTitle, nodeContent, language, onClose, onFallback,
}) => {
    const isVi = language === 'vietnamese';
    const [phase, setPhase] = useState<Phase>('connecting');
    const [expanded, setExpanded] = useState(false);
    const [muted, setMuted] = useState(false);
    const [transcript, setTranscript] = useState<{ role: 'ai' | 'user'; text: string }[]>([]);
    const [errorMsg, setErrorMsg] = useState('');

    // Refs — never trigger re-renders
    const sessionRef = useRef<any>(null);
    const audioCtxRef = useRef<AudioContext | null>(null);
    const nextPlayTimeRef = useRef(0);
    const activeSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
    const workletNodeRef = useRef<AudioWorkletNode | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const mutedRef = useRef(false);
    const phaseRef = useRef<Phase>('connecting');
    const aiTextBufRef = useRef('');
    const scrollRef = useRef<HTMLDivElement>(null);

    phaseRef.current = phase;
    mutedRef.current = muted;

    const scrollToBottom = () => setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);

    // ── Playback ─────────────────────────────────────────────────────────
    const stopPlayback = useCallback(() => {
        activeSourcesRef.current.forEach(src => { try { src.stop(); } catch {} });
        activeSourcesRef.current.clear();
        if (audioCtxRef.current) nextPlayTimeRef.current = audioCtxRef.current.currentTime;
    }, []);

    const playPCMChunk = useCallback((base64: string) => {
        if (mutedRef.current || !audioCtxRef.current) return;
        const ctx = audioCtxRef.current;

        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

        const int16 = new Int16Array(bytes.buffer);
        const float32 = new Float32Array(int16.length);
        for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 32768;

        if (float32.length === 0) return;

        const buf = ctx.createBuffer(1, float32.length, 24000);
        buf.getChannelData(0).set(float32);
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.connect(ctx.destination);
        activeSourcesRef.current.add(src);
        src.onended = () => activeSourcesRef.current.delete(src);

        const startAt = Math.max(ctx.currentTime + 0.02, nextPlayTimeRef.current);
        src.start(startAt);
        nextPlayTimeRef.current = startAt + buf.duration;
    }, []);

    // ── Mic capture via AudioWorklet ─────────────────────────────────────
    const startMic = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: { sampleRate: 16000, channelCount: 1, echoCancellation: true, noiseSuppression: true },
            });
            streamRef.current = stream;

            const ctx = new AudioContext({ sampleRate: 16000 });
            audioCtxRef.current = ctx;
            nextPlayTimeRef.current = 0;

            await ctx.audioWorklet.addModule('/audio-worklet.js');
            const source = ctx.createMediaStreamSource(stream);
            const worklet = new AudioWorkletNode(ctx, 'pcm-audio-processor');
            workletNodeRef.current = worklet;

            worklet.port.onmessage = (e: MessageEvent) => {
                if (!sessionRef.current || phaseRef.current === 'error') return;
                const int16 = new Int16Array(e.data);
                // Convert to base64 for Gemini Live realtimeInput
                const bytes = new Uint8Array(int16.buffer);
                let binary = '';
                for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
                const base64 = btoa(binary);
                try {
                    sessionRef.current.sendRealtimeInput({
                        audio: { data: base64, mimeType: 'audio/pcm;rate=16000' },
                    });
                } catch {}
            };

            source.connect(worklet);
            // worklet output is discarded (we only want the side-effect of capturing)
        } catch (e: any) {
            setPhase('error');
            setErrorMsg(isVi ? 'Không thể mở mic.' : 'Could not open mic.');
        }
    }, [isVi]);

    const stopMic = useCallback(() => {
        workletNodeRef.current?.disconnect();
        workletNodeRef.current = null;
        streamRef.current?.getTracks().forEach(t => t.stop());
        streamRef.current = null;
    }, []);

    // ── Gemini Live session ──────────────────────────────────────────────
    const connect = useCallback(async () => {
        setPhase('connecting');
        setErrorMsg('');

        let token: string;
        try {
            token = await getVoiceTutorLiveToken(nodeTitle, nodeContent, language);
        } catch (e: any) {
            // Token generation failed → fall back to basic mode
            onFallback();
            return;
        }

        try {
            const aiClient = new GoogleGenAI({ apiKey: token });

            const session = await (aiClient as any).live.connect({
                model: LIVE_MODEL,
                config: {
                    responseModalities: ['AUDIO'],
                },
                callbacks: {
                    onopen: () => {
                        setPhase('listening');
                        startMic();
                    },

                    onmessage: (msg: any) => {
                        const sc = msg.serverContent;
                        if (!sc) return;

                        // Audio chunks from AI
                        const parts = sc.modelTurn?.parts ?? [];
                        for (const part of parts) {
                            if (part.inlineData?.data) {
                                if (phaseRef.current !== 'ai-speaking') setPhase('ai-speaking');
                                playPCMChunk(part.inlineData.data);
                            }
                            // Transcript text (if any)
                            if (part.text?.trim()) {
                                aiTextBufRef.current += part.text;
                            }
                        }

                        // AI finished speaking this turn
                        if (sc.turnComplete) {
                            if (aiTextBufRef.current.trim()) {
                                setTranscript(prev => [...prev, { role: 'ai', text: aiTextBufRef.current.trim() }]);
                                scrollToBottom();
                                aiTextBufRef.current = '';
                            }
                            setPhase('listening');
                        }

                        // User interrupted AI — stop audio, go back to listening
                        if (sc.interrupted) {
                            stopPlayback();
                            if (aiTextBufRef.current.trim()) {
                                setTranscript(prev => [...prev, { role: 'ai', text: aiTextBufRef.current.trim() + ' [interrupted]' }]);
                                aiTextBufRef.current = '';
                            }
                            setPhase('user-speaking');
                        }

                        // Input transcript (what user said)
                        if (msg.toolCall || msg.setupComplete) return;
                        const inputText = sc.inputTranscription?.text?.trim();
                        if (inputText) {
                            setTranscript(prev => [...prev, { role: 'user', text: inputText }]);
                            scrollToBottom();
                        }
                    },

                    onerror: (e: any) => {
                        setPhase('error');
                        setErrorMsg(e?.message || (isVi ? 'Lỗi kết nối.' : 'Connection error.'));
                    },

                    onclose: () => {
                        if (phaseRef.current !== 'error') setPhase('listening');
                    },
                },
            });

            sessionRef.current = session;
        } catch (e: any) {
            setPhase('error');
            setErrorMsg(isVi ? `Không thể kết nối Live API: ${e.message}` : `Live API error: ${e.message}`);
        }
    }, [nodeTitle, nodeContent, language, startMic, playPCMChunk, stopPlayback, onFallback, isVi]);

    useEffect(() => {
        connect();
        return () => {
            stopMic();
            stopPlayback();
            try { sessionRef.current?.close(); } catch {}
        };
    }, []);

    const toggleMute = () => {
        const next = !muted;
        setMuted(next); mutedRef.current = next;
        if (next) stopPlayback();
    };

    const handleOrbTap = () => {
        if (phase === 'ai-speaking') {
            // Interrupt: user can tap orb to interrupt AI
            stopPlayback();
            setPhase('listening');
        } else if (phase === 'error') {
            connect();
        }
        // In listening/user-speaking phase, Gemini VAD handles everything — no tap needed
    };

    const aiSpeaking = phase === 'ai-speaking';
    const userSpeaking = phase === 'user-speaking';
    const connecting = phase === 'connecting';
    const listening = phase === 'listening';

    const widget = (
        <div className="fixed bottom-6 right-6 z-[600] flex flex-col items-end gap-2 select-none">

            {/* ── Expanded chat panel ── */}
            {expanded && (
                <div className="w-80 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 flex flex-col overflow-hidden animate-in slide-in-from-bottom-2 duration-200"
                    style={{ maxHeight: '58vh' }}>

                    <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-slate-800 shrink-0">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shrink-0">
                            <Zap size={12} className="text-white" />
                        </div>
                        <p className="flex-1 text-xs font-semibold text-gray-700 dark:text-gray-200 truncate">{nodeTitle || 'Live Tutor'}</p>
                        <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-1.5 py-0.5 rounded-full">LIVE</span>
                        <button onClick={toggleMute} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400 transition-colors">
                            {muted ? <VolumeX size={13} /> : <Volume2 size={13} />}
                        </button>
                        <button onClick={() => setExpanded(false)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400 transition-colors">
                            <ChevronDown size={13} />
                        </button>
                        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400 transition-colors">
                            <X size={13} />
                        </button>
                    </div>

                    {/* Transcript */}
                    <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 min-h-0">
                        {transcript.length === 0 && !connecting && (
                            <p className="text-xs text-gray-400 text-center py-4 italic">
                                {isVi ? 'Cứ nói tự nhiên — AI đang nghe...' : 'Just speak naturally — AI is listening...'}
                            </p>
                        )}
                        {transcript.map((msg, i) => (
                            <div key={i} className={`flex gap-1.5 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                                {msg.role === 'ai' && (
                                    <div className="w-5 h-5 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shrink-0 mt-0.5">
                                        <Zap size={10} className="text-white" />
                                    </div>
                                )}
                                <div className={`max-w-[85%] px-3 py-2 rounded-xl text-xs leading-relaxed ${
                                    msg.role === 'ai'
                                        ? 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-tl-sm'
                                        : 'bg-emerald-600 text-white rounded-tr-sm'
                                }`}>{msg.text}</div>
                            </div>
                        ))}

                        {connecting && (
                            <div className="flex items-center justify-center gap-2 py-4">
                                <div className="flex gap-1">
                                    {[0, 150, 300].map(d => <span key={d} className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />)}
                                </div>
                                <span className="text-xs text-gray-400">{isVi ? 'Đang kết nối...' : 'Connecting...'}</span>
                            </div>
                        )}
                        {phase === 'error' && <p className="text-xs text-red-500 text-center py-2">{errorMsg}</p>}
                        <div ref={scrollRef} />
                    </div>

                    {/* Status bar */}
                    <div className="px-4 py-2 border-t border-gray-100 dark:border-slate-800 shrink-0">
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                            {aiSpeaking ? (isVi ? '🔊 AI đang nói — cứ ngắt tự nhiên...' : '🔊 AI speaking — interrupt anytime...')
                                : userSpeaking ? (isVi ? '🎙 Đang nghe bạn...' : '🎙 Listening to you...')
                                    : listening ? (isVi ? '💬 Cứ nói...' : '💬 Go ahead, speak...')
                                        : connecting ? (isVi ? 'Đang kết nối Gemini Live...' : 'Connecting to Gemini Live...')
                                            : ''}
                        </p>
                    </div>
                </div>
            )}

            {/* ── Floating orb row ── */}
            <div className="flex items-center gap-2">
                {!expanded && (
                    <button onClick={() => setExpanded(true)}
                        className="text-xs text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 transition-colors px-2.5 py-1.5 rounded-full bg-white/90 dark:bg-slate-800/90 backdrop-blur shadow border border-emerald-200/80 dark:border-emerald-800/80 flex items-center gap-1">
                        <ChevronUp size={11} />
                        <span>{isVi ? 'Chat' : 'Chat'}</span>
                        <span className="text-[9px] font-bold bg-emerald-500 text-white px-1 rounded-full">LIVE</span>
                    </button>
                )}

                {/* Live orb — teal/emerald color to distinguish from basic tutor */}
                <button onClick={handleOrbTap} className="relative flex items-center justify-center" style={{ width: 64, height: 64 }}>

                    {/* Ambient rings */}
                    {(aiSpeaking || userSpeaking) && (
                        <span className="absolute inset-0 rounded-full animate-ping opacity-20"
                            style={{ background: aiSpeaking ? 'rgb(16,185,129)' : 'rgb(99,102,241)' }} />
                    )}
                    {connecting && (
                        <span className="absolute inset-0 rounded-full bg-emerald-400 animate-pulse opacity-30" />
                    )}

                    {/* Orb face */}
                    <div className={`w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-300
                        ${aiSpeaking
                            ? 'bg-gradient-to-br from-emerald-400 to-teal-600 scale-110'
                            : userSpeaking
                                ? 'bg-gradient-to-br from-indigo-500 to-violet-600 scale-105'
                                : connecting
                                    ? 'bg-gradient-to-br from-emerald-400 to-teal-500'
                                    : phase === 'error'
                                        ? 'bg-red-500'
                                        : 'bg-gradient-to-br from-emerald-500 to-teal-600 hover:scale-105'
                        }`}>

                        {aiSpeaking ? (
                            <div className="flex items-center gap-0.5">
                                {[14, 20, 10, 22, 12].map((h, i) => (
                                    <span key={i} className="w-0.5 bg-white rounded-full animate-pulse"
                                        style={{ height: h, animationDelay: `${i * 90}ms`, animationDuration: '0.65s' }} />
                                ))}
                            </div>
                        ) : userSpeaking ? (
                            <div className="flex items-center gap-0.5">
                                {[8, 16, 12, 18, 10].map((h, i) => (
                                    <span key={i} className="w-0.5 bg-white/80 rounded-full animate-pulse"
                                        style={{ height: h, animationDelay: `${i * 70}ms`, animationDuration: '0.5s' }} />
                                ))}
                            </div>
                        ) : connecting ? (
                            <div className="flex items-end gap-0.5 pb-0.5">
                                {[0, 150, 300].map(d => <span key={d} className="w-1 h-1 bg-white/80 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />)}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center gap-0.5">
                                <Zap size={18} className="text-white" />
                            </div>
                        )}
                    </div>

                    {/* Close X */}
                    <button onClick={(e) => { e.stopPropagation(); onClose(); }}
                        className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-full shadow flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors">
                        <X size={10} />
                    </button>
                </button>
            </div>
        </div>
    );

    return createPortal(widget, document.body);
};
